import { useEffect, useRef, useState } from 'react';

import { getErrorMessage } from '@/api';
import type { RequestEvent, RequestRecord } from '@/api';
import { fetchResellerRefreshStatus, startResellerRefresh } from '@/api/networkMeta';
import type { ResellerRefreshStatus } from '@/api/networkMeta';
import { fetchResellerAllowedRefreshStatus, startResellerAllowedRefresh } from '@/api/resellerAllowed';
import { fetchRequest } from '@/api/requests';
import type { ProcessStepItem } from '@/components/ui';

// Admon 네트워크 승인(또는 재시도)·관리자 수동 동기화 뒤 GAM 매핑 동기화가 어디까지 왔는지 단계별로 보여 준다.
//
// 단계: 메타데이터 반영 → GAM 매핑 동기화 요청(Dagster job) → 매핑 → 리셀러 kpi 재생성.
// 서버는 동기화를 받아들이면 응답 전에 reseller_refresh_queued 를 남기고, 이후 결과는
// 백그라운드 스레드가 reseller_refresh_* 이벤트로 남긴다. 승인·재시도는 요청 상세를, 수동 동기화는
// 상태 API 의 수동 실행 이벤트를 다시 읽어 '시작 시점 이후' 이벤트만 보고 단계를 옮긴다.
// 창을 닫아도 서버 실행은 계속된다. 리셀러 허용 승인은 target 만 바꿔 같은 추적을 쓴다(runRetry 는 GAM 전용).

const FAST_POLL_MS = 2_000;
const SLOW_POLL_MS = 10_000;
const FAST_POLLS = 15;
// 상태 조회가 이 횟수만큼 연달아 실패하면 추적을 멈춘다(실행은 서버에서 계속된다).
const MAX_FETCH_ERRORS = 5;
// 대기 이벤트가 안 보일 때 '건너뜀'으로 단정하기 전에 다시 볼 횟수 — 기록이 잠깐 늦을 수 있다.
const QUEUED_GRACE_POLLS = 3;
// 추적 상한 — 서버는 잠금 대기와 run 대기에 각각 제한(기본 30분)을 두므로 그보다 넉넉히.
const TRACK_LIMIT_MS = 70 * 60 * 1000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const STEP_RUN_DESC = '보통 수 분 걸려요 — 창을 닫아도 계속 진행돼요';

// 따라갈 대상 — 기본은 GAM 매핑 동기화다. 리셀러 허용(화면의 수동 실행과 요청 승인)은 같은 추적에
// 엔드포인트·이벤트 접두·단계 문구만 바꿔 쓴다(서버의 상태 모델이 같다).
export interface ManualRefreshTarget {
  // 승인·재시도의 첫 단계(정본 반영) 문구와 그 실패 문구.
  applyLabel: string;
  applyDesc: string;
  applyFailedText: string;
  // 승인 뒤 대기 이벤트가 끝내 안 보일 때 — 이번 반영은 재생성 대상이 아니었다.
  skippedText: string;
  title: string;
  launchLabel: string;
  launchDesc: string;
  runLabel: string;
  failedText: string;
  // 이 실행의 이벤트 타입 접두. 뒤에 queued·dry_run·started·succeeded·failed 가 붙는다.
  eventPrefix: string;
  fetchStatus: (signal?: AbortSignal) => Promise<ResellerRefreshStatus>;
  // requestId 를 주면 그 승인 요청의 재시도다(이벤트가 요청에 남는다).
  start: (requestId?: number) => Promise<unknown>;
}

export const GAM_REFRESH_TARGET: ManualRefreshTarget = {
  applyLabel: '메타데이터 반영',
  applyDesc: 'Admon 네트워크 정본(BigQuery)',
  applyFailedText: 'Admon 네트워크 반영에 실패했어요',
  skippedText: 'GAM 코드 변경이 없거나 동기화가 꺼져 있어 건너뛰었어요',
  title: 'GAM 매핑 동기화',
  launchLabel: 'GAM 매핑 동기화 요청',
  launchDesc: 'Dagster gam_reseller_job 실행 요청',
  runLabel: 'GAM 매핑 → 리셀러 kpi 재생성',
  failedText: 'GAM 매핑 동기화에 실패했어요',
  eventPrefix: 'reseller_refresh_',
  fetchStatus: fetchResellerRefreshStatus,
  start: (requestId) => startResellerRefresh(requestId),
};

// 리셀러 허용 재생성 — 승인 반영 뒤 서버가 자동으로 걸고, 요청 상세의 재시도는 그게 실패했을 때 쓴다.
export const RESELLER_ALLOWED_REFRESH_TARGET: ManualRefreshTarget = {
  applyLabel: '허용 목록 반영',
  applyDesc: '리셀러 허용 정본(BigQuery)',
  applyFailedText: '리셀러 허용 반영에 실패했어요',
  skippedText: '재생성이 꺼져 있어 건너뛰었어요 — 매일 10시 정기 실행 때 반영돼요',
  title: '리셀러 허용 재생성',
  launchLabel: '재생성 요청',
  launchDesc: 'Dagster reseller_allowed_job 실행 요청',
  runLabel: '정본 → reseller_allowed 재생성',
  failedText: '리셀러 허용 재생성에 실패했어요',
  eventPrefix: 'reseller_allowed_refresh_',
  fetchStatus: fetchResellerAllowedRefreshStatus,
  start: (requestId) => startResellerAllowedRefresh(requestId),
};

function initialSteps(withMeta: boolean, target: ManualRefreshTarget): ProcessStepItem[] {
  const steps: ProcessStepItem[] = [
    { key: 'launch', label: target.launchLabel, desc: target.launchDesc, status: 'pending' },
    { key: 'run', label: target.runLabel, desc: STEP_RUN_DESC, status: 'pending' },
  ];
  return withMeta
    ? [{ key: 'meta', label: target.applyLabel, desc: target.applyDesc, status: 'active' }, ...steps]
    : [{ ...steps[0], status: 'active' }, steps[1]];
}

export function latestEventId(record: RequestRecord): number {
  return record.events.reduce((max, ev) => Math.max(max, ev.id), 0);
}

// 이 요청의 가장 최근 재생성 이벤트(없으면 undefined). 기본은 GAM 동기화, 리셀러 허용은 그 접두를 준다.
export function latestGamSyncEvent(
  record: RequestRecord,
  prefix: string = GAM_REFRESH_TARGET.eventPrefix,
): RequestEvent | undefined {
  return [...record.events]
    .filter((ev) => ev.event_type.startsWith(prefix))
    .sort((a, b) => b.id - a.id)[0];
}

export interface GamSyncProgress {
  steps: ProcessStepItem[] | null;
  title: string;
  close: () => void;
  // 승인 또는 메타데이터 반영 재시도 — apply 결과가 실패면 첫 단계에서 멈춘다.
  // onApplied 는 메타데이터 반영이 성공한 즉시 부른다(GAM 추적을 기다리지 않는다).
  runApply: (
    title: string,
    apply: () => Promise<RequestRecord>,
    baselineId: number,
    onApplied?: () => void,
  ) => Promise<RequestRecord | null>;
  // 메타데이터는 반영됐고 GAM 동기화만 다시 돌릴 때.
  runRetry: (requestId: number) => Promise<void>;
  // Admon 네트워크 화면의 관리자 수동 동기화.
  runManual: () => Promise<void>;
}

// 추적에 필요한 이벤트 모양 — 요청 이벤트와 상태 API 이벤트가 함께 맞춘다.
interface SyncEvent {
  id: number;
  event_type: string;
  note: string | null;
}

export function useGamSyncProgress(
  onSettled: () => void,
  target: ManualRefreshTarget = GAM_REFRESH_TARGET,
): GamSyncProgress {
  const [steps, setSteps] = useState<ProcessStepItem[] | null>(null);
  const [title, setTitle] = useState('');
  // 창을 닫으면 추적만 멈춘다 — 다음 실행의 추적과 섞이지 않게 실행마다 번호를 올린다.
  const runToken = useRef(0);
  // 화면을 떠나면 진행 중인 추적을 멈춘다.
  useEffect(() => () => {
    runToken.current += 1;
  }, []);

  const patch = (key: string, status: ProcessStepItem['status'], desc?: string) =>
    setSteps((prev) =>
      prev ? prev.map((s) => (s.key === key ? { ...s, status, ...(desc !== undefined ? { desc } : {}) } : s)) : prev,
    );

  // 진행 중(active)인 단계를 모두 실패로 정리한다 — 실패와 진행 중이 함께 보이지 않게.
  const failActive = (desc: string) =>
    setSteps((prev) => (prev ? prev.map((s) => (s.status === 'active' ? { ...s, status: 'failed', desc } : s)) : prev));

  const requestEvents = (requestId: number) => async (): Promise<SyncEvent[]> =>
    (await fetchRequest(requestId)).events;
  // 상태 API 는 승인 훅 실행도 함께 보여 준다 — 이 화면에서 시작한 수동 실행만 따라간다.
  const manualEvents = async (): Promise<SyncEvent[]> =>
    (await target.fetchStatus()).events.filter((ev) => ev.trigger === 'manual');

  const track = async (
    fetchEvents: () => Promise<SyncEvent[]>,
    baselineId: number,
    token: number,
    expectQueued: boolean,
  ) => {
    let polls = 0;
    let fetchErrors = 0;
    const deadline = Date.now() + TRACK_LIMIT_MS;
    while (runToken.current === token) {
      if (Date.now() > deadline) {
        failActive('진행 상태 확인 시간을 넘겼어요 — 결과는 이벤트 로그에서 확인하세요');
        return;
      }
      let fetched: SyncEvent[];
      try {
        fetched = await fetchEvents();
        fetchErrors = 0;
      } catch (err) {
        fetchErrors += 1;
        if (fetchErrors >= MAX_FETCH_ERRORS) {
          failActive(`진행 상태를 불러오지 못했어요: ${getErrorMessage(err)} — 결과는 이벤트 로그에서 확인하세요`);
          return;
        }
        await sleep(SLOW_POLL_MS);
        continue;
      }
      if (runToken.current !== token) return;
      const prefix = target.eventPrefix;
      const events = fetched.filter((ev) => ev.id > baselineId && ev.event_type.startsWith(prefix));
      const types = new Set(events.map((ev) => ev.event_type.slice(prefix.length)));
      const failed = events.find((ev) => ev.event_type === `${prefix}failed`);
      const started = events.find((ev) => ev.event_type === `${prefix}started`);

      if (events.length === 0 && expectQueued && polls >= QUEUED_GRACE_POLLS) {
        // 대기 이벤트는 승인 응답 전에 남는다 — 몇 번 봐도 없으면 이번 승인은 동기화 대상이 아니다.
        patch('launch', 'done', target.skippedText);
        patch('run', 'done', '재생성할 필요가 없어요');
        return;
      }
      if (types.has('dry_run')) {
        patch('launch', 'done', '점검 모드(dry-run) — 실제 실행 요청은 하지 않았어요');
        patch('run', 'done', '점검 모드라 재생성하지 않았어요');
        return;
      }
      if (failed) {
        patch(started ? 'run' : 'launch', 'failed', failed.note ?? target.failedText);
        if (started) patch('launch', 'done', started.note ?? undefined);
        return;
      }
      if (started) {
        patch('launch', 'done', started.note ?? undefined);
        patch('run', 'active');
      } else {
        patch('launch', 'active');
      }
      if (types.has('succeeded')) {
        patch('run', 'done', '대시보드에 반영됐어요');
        return;
      }
      polls += 1;
      await sleep(polls <= FAST_POLLS ? FAST_POLL_MS : SLOW_POLL_MS);
    }
  };

  const runApply: GamSyncProgress['runApply'] = async (nextTitle, apply, baselineId, onApplied) => {
    const token = ++runToken.current;
    setTitle(nextTitle);
    setSteps(initialSteps(true, target));
    let applied: RequestRecord;
    try {
      applied = await apply();
    } catch (err) {
      patch('meta', 'failed', getErrorMessage(err));
      return null;
    }
    if (applied.bq_sync_status === 'failed') {
      patch('meta', 'failed', applied.bq_sync_error ?? target.applyFailedText);
      onSettled();
      return applied;
    }
    patch('meta', 'done');
    patch('launch', 'active');
    onApplied?.();
    onSettled();
    await track(requestEvents(applied.id), baselineId, token, true);
    // 창을 닫거나 화면을 떠났으면(토큰이 바뀜) 정리는 그쪽이 이미 했다.
    if (runToken.current === token) onSettled();
    return applied;
  };

  const runRetry: GamSyncProgress['runRetry'] = async (requestId) => {
    const token = ++runToken.current;
    setTitle(`${target.title} 재시도`);
    setSteps(initialSteps(false, target));
    let baselineId: number;
    try {
      // 기준 이벤트는 누르는 순간의 최신 상태로 잡는다 — 화면의 요청 데이터는 오래됐을 수 있다.
      baselineId = latestEventId(await fetchRequest(requestId));
      await target.start(requestId);
    } catch (err) {
      patch('launch', 'failed', getErrorMessage(err));
      return;
    }
    await track(requestEvents(requestId), baselineId, token, false);
    if (runToken.current === token) onSettled();
  };

  const runManual: GamSyncProgress['runManual'] = async () => {
    const token = ++runToken.current;
    setTitle(target.title);
    setSteps(initialSteps(false, target));
    let baselineId: number;
    try {
      // 기준 이벤트는 누르는 순간의 최신 상태로 잡는다.
      baselineId = (await target.fetchStatus()).events.reduce((max, ev) => Math.max(max, ev.id), 0);
      await target.start();
    } catch (err) {
      patch('launch', 'failed', getErrorMessage(err));
      return;
    }
    onSettled();
    await track(manualEvents, baselineId, token, false);
    if (runToken.current === token) onSettled();
  };

  const close = () => {
    runToken.current += 1;
    setSteps(null);
    onSettled();
  };

  return { steps, title, close, runApply, runRetry, runManual };
}
